/** Ergebnis des letzten Prüflaufs einer Nachverfolgung, in Klartext.
 *
 *  Das Backend schreibt bei **jedem** Lauf `data.lastCheck = { at, status }`
 *  (#1315 — Status-Marker statt Fehler-Mails). Diese Auskunft stand bisher nur
 *  im Ergebnis-Dialog, und der war ausschließlich bei *geschlossenen*
 *  Nachverfolgungen erreichbar. Eine offene zeigte in der Übersicht nur ein
 *  Kreuz. Die QS dazu: „ich bekomme keine Info darüber, was genau die Prüfung
 *  erbracht hat und was dann zu tun ist" (qs/dino-qs#24).
 *
 *  Hier liegt die Bewertung einmal, damit Übersicht und Dialog dasselbe sagen.
 */

export type LastCheckSeverity = 'success' | 'error' | 'info';

export interface LastCheckSummary {
  status: string;
  label: string;
  severity: LastCheckSeverity;
  at?: string;
}

/** Klartext je Status. */
export const LAST_CHECK_LABELS: Record<string, string> = {
  ok_baseline: 'Baseline erfasst',
  ok_no_change: 'Geprüft — keine Änderung',
  entry_found: 'Eintragung gefunden',
  change_found: 'Veränderung gefunden',
  deletion_found: 'Löschung erkannt',
  gl_found: 'Neue Gesellschafterliste',
  absent: 'Firma (noch) nicht im Register',
  search_failed: 'Suche fehlgeschlagen',
  si_download_failed: 'SI-Abruf fehlgeschlagen',
  si_parse_failed: 'SI nicht auswertbar',
  dk_failed: 'Dokumentenansicht nicht erreichbar',
  gl_list_missing: 'Keine Gesellschafterlisten-Mappe gefunden',
  client_missing: 'Firmendaten fehlen',
  unreachable: 'Portal nicht erreichbar',
};

/** Die Prüfung selbst ist gescheitert — nicht zu verwechseln mit „geprüft,
 *  aber nichts gefunden". Das war der Anlass für #1315. */
export const FAILURE_STATUSES = new Set([
  'search_failed',
  'si_download_failed',
  'si_parse_failed',
  'dk_failed',
  'gl_list_missing',
  'client_missing',
  'unreachable',
]);

/** Die Prüfung hat einen Fund ergeben. */
const SUCCESS_STATUSES = new Set([
  'entry_found',
  'change_found',
  'deletion_found',
  'gl_found',
]);

const severityOf = (status: string): LastCheckSeverity => {
  if (FAILURE_STATUSES.has(status)) return 'error';
  if (SUCCESS_STATUSES.has(status)) return 'success';
  // Alles Übrige ist ein Zwischenstand: Baseline erfasst, keine Änderung,
  // „noch nicht im Register" — und auch ein künftiger, hier noch unbekannter
  // Status. Ihn zu verschweigen war der ursprüngliche Fehler.
  return 'info';
};

/** `data` der Nachverfolgung auswerten. `null`, wenn noch nie geprüft wurde.
 *  Nimmt das Feld als Objekt **oder** als JSON-String — beides kommt vor. */
export const lastCheckSummary = (rawData: unknown): LastCheckSummary | null => {
  let data: any = rawData;

  if (typeof data === 'string') {
    try {
      data = JSON.parse(data);
    } catch {
      return null;
    }
  }

  const status = data?.lastCheck?.status;
  if (typeof status !== 'string' || status === '') return null;

  return {
    status,
    label: LAST_CHECK_LABELS[status] ?? status,
    severity: severityOf(status),
    at: data.lastCheck.at,
  };
};
